/**
 * 标准配合独立求解器。
 *
 * 文件职责：
 * 1. 只消费装配文档 plain data、组件 transform 和 Mate 定义；
 * 2. 输出新的组件 transform 与求解诊断；
 * 3. 不依赖 Viewer、UI、Object3D、持久化 Store 或命令系统。
 */

import * as THREE from 'three';
import type { ComponentTransformSnapshot } from '../component-instance';
import {
  AssemblyMateType,
  MateAlignment,
  MateConcentricMisalignmentType,
  MateSolveStatus,
  cloneComponentTransformSnapshot,
  type AssemblyMateDefinition,
  type MateEntityRef,
} from '../definition';
import { resolveAssemblyReferenceDependencySolveIssue } from '../reference-dependency';
import { resolveAssemblyMateComponentInstanceIds } from '../utils/AssemblyMateEntities';
import type {
  AssemblyMateSolveDiagnostic,
  AssemblyMateSolverComponentState,
  StandardMateApplyResult,
  StandardMateSolverInput,
  StandardMateSolverResult,
  StandardMateMovingSideResolution,
} from './StandardMateSolverTypes';
import { StandardMateDistanceBranchPolicy } from './StandardMateSolverTypes';
import {
  createComponentWorldDeltaMap,
  transformMateDefinitionForSolve,
  updateComponentWorldDeltaMap,
} from './geometry/StandardMateGeometryTransform';
import { resolveStandardMateLinearDimensionScale } from './dimension/StandardMateDimension';
import { validateSolvedMateDefinitions } from './validation/StandardMateConstraintValidator';
import { collectStandardMateRigidInvariantViolations } from './validation/StandardMateRigidInvariantGuard';
import {
  applyWorldDeltaToTransform,
  areTransformsClose,
  createRotationDeltaBetweenDirections,
  createRotationDeltaAroundPoint,
  createTranslationDelta,
  composeMatrix,
  decomposeMatrix,
  normalizeVector,
  resolveAxisGeometry,
  resolvePlaneGeometry,
  resolvePerpendicularDirection,
} from './geometry/StandardMateGeometryAccess';
import { transformStandardMateWorldBounds } from './geometry/StandardMateWorldBounds';
import { solveTangentMate } from './StandardMateTangentSolver';
import {
  solveConcentricMate,
  solveConcentricMatePair,
  solveLockMate,
  solveSymmetricMisalignedConcentricMate,
} from './StandardMateConcentricLockSolver';
import { createStandardMateSolvePlan } from './StandardMateSolvePlan';
import {
  resolveDistanceOffsetsForBranchPolicy,
  selectContinuousDistanceResult,
  solveCoincidentOrDistanceOffset,
} from './StandardMateDistanceSolver';
import { resolveStandardMateSolverOverallStatus } from './StandardMateSolverStatus';
import {
  resolveStandardMateConstraintGraphSolve,
  resolveStandardMateCurrentResidualSolve,
} from './StandardMateConstraintGraphSolver';
import {
  DEFAULT_STANDARD_MATE_LINEAR_TOLERANCE,
  resolveStandardMateConstraintTolerance,
} from './StandardMateConstraintResidual';
import {
  resolveStandardMateRigidComponentGroups,
  type StandardMateRigidComponentGroups,
} from './StandardMateRigidGroup';
import {
  appendStandardMateSolverDebugTraceEvent,
  createStandardMateSolverDebugTraceSession,
  emitStandardMateSolverDebugTrace,
} from './StandardMateSolverDebugTrace';

/**
 * 求解容差。
 *
 * 必须与残差“是否满足”的判定容差 {@link DEFAULT_STANDARD_MATE_LINEAR_TOLERANCE} 一致：主投影校验若比
 * 残差定义更严（曾为 1e-7 < 1e-6），会把几何上已满足（残差 < 1e-6）的已提交配合误判为“求解后不满足”，
 * 且与约束图求解、残差优先接受用的 1e-6 相互矛盾——同一姿态在图求解处报 solved、在主校验处报 unsolved，
 * 逐次旋转把这类“已提交却被判越界”的姿态写回并累积，几次后触发“突然转不动 + 新增同轴心报破坏已提交配合”。
 * 全链路统一到单一容差源，从底层消除判据不一致。导出以便测试守卫“默认容差不得再次分叉”。
 */
export const DEFAULT_SOLVER_EPSILON = DEFAULT_STANDARD_MATE_LINEAR_TOLERANCE;

/** 拖动场景下局部配合图的最大迭代轮数。 */
const DEFAULT_SOLVER_MAX_ITERATIONS = 8;

/** 单条配合应用结果。 */
type ApplyMateResult = StandardMateApplyResult;

/** 移动侧解析结果。 */
type MovingSideResolution = StandardMateMovingSideResolution;

/**
 * 同轴心错位求解策略。
 */
interface ConcentricMisalignmentSolvePolicy {
  /** 当前 Mate 在求解阶段只参与最终偏差校验，不主动移动组件。 */
  readonly skipSolve?: boolean;
  /** 当前 Mate 需要与指定 linked Mate 平均分摊偏差。 */
  readonly symmetricLinkedMateId?: string;
}

/**
 * 判断组件是否固定。
 *
 * @param component 组件状态。
 * @returns 是否固定。
 */
function isComponentFixed(component: AssemblyMateSolverComponentState): boolean {
  return component.fixationState === 'fixed';
}

/**
 * 克隆 transform 映射。
 *
 * @param components 组件状态。
 * @returns transform 映射。
 */
function cloneComponentTransforms(
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
): Record<string, ComponentTransformSnapshot> {
  const result: Record<string, ComponentTransformSnapshot> = {};
  Object.values(components).forEach((component) => {
    result[component.componentInstanceId] = cloneComponentTransformSnapshot(component.transform);
  });
  return result;
}

/**
 * 判断 Mate 是否为具备 linkedMateId 的同轴心错位 Mate。
 *
 * @param mate Mate Definition。
 * @returns 是否为可参与错位策略的 Concentric Mate。
 */
function isLinkedConcentricMisalignmentMate(mate: AssemblyMateDefinition): boolean {
  return mate.mateType === AssemblyMateType.Concentric
    && typeof mate.concentricMisalignment?.linkedMateId === 'string'
    && mate.concentricMisalignment.linkedMateId.length > 0;
}

/**
 * 登记同轴心错位求解策略。
 *
 * @param policies 策略表。
 * @param mateId Mate ID。
 * @param policy 策略片段。
 */
function appendConcentricMisalignmentSolvePolicy(
  policies: Map<string, ConcentricMisalignmentSolvePolicy>,
  mateId: string,
  policy: ConcentricMisalignmentSolvePolicy,
): void {
  const existing = policies.get(mateId) ?? {};
  policies.set(mateId, {
    ...existing,
    ...policy,
  });
}

/**
 * 构建 SOLIDWORKS 同轴心错位求解策略表。
 *
 * 设计说明：
 * Misaligned concentric 是一对 Concentric Mate 的关系：
 * - Align linked mate：当前 Mate 只校验偏差，linked Mate 精确求解；
 * - Align this mate：当前 Mate 精确求解，linked Mate 只校验偏差；
 * - Symmetric：linked Mate 不再精确拉回，当前 Mate 用当前/linked 两个精确解的中点求解。
 *
 * @param mateDefinitions 本轮求解 Mate 列表。
 * @returns 按 Mate ID 索引的求解策略。
 */
function createConcentricMisalignmentSolvePolicyMap(
  mateDefinitions: readonly AssemblyMateDefinition[],
): ReadonlyMap<string, ConcentricMisalignmentSolvePolicy> {
  const mateIds = new Set(mateDefinitions.map((mate) => mate.mateId));
  const policies = new Map<string, ConcentricMisalignmentSolvePolicy>();

  mateDefinitions.forEach((mate) => {
    if (!isLinkedConcentricMisalignmentMate(mate)) {
      return;
    }
    const linkedMateId = mate.concentricMisalignment!.linkedMateId;
    if (!mateIds.has(linkedMateId)) {
      return;
    }

    if (mate.concentricMisalignment!.type === MateConcentricMisalignmentType.AlignLinkedMate) {
      appendConcentricMisalignmentSolvePolicy(policies, mate.mateId, { skipSolve: true });
      return;
    }
    if (mate.concentricMisalignment!.type === MateConcentricMisalignmentType.AlignThisMate) {
      appendConcentricMisalignmentSolvePolicy(policies, linkedMateId, { skipSolve: true });
      return;
    }
    if (mate.concentricMisalignment!.type === MateConcentricMisalignmentType.Symmetric) {
      appendConcentricMisalignmentSolvePolicy(policies, linkedMateId, { skipSolve: true });
      appendConcentricMisalignmentSolvePolicy(policies, mate.mateId, {
        symmetricLinkedMateId: linkedMateId,
      });
    }
  });

  return policies;
}

/**
 * 判断当前移动组件是否已经被已提交 Mate 约束。
 *
 * 设计说明：
 * PropertyManager 临时预览可以为了视觉贴合选择一个代表点初始解，但这类移动不是
 * 标准 Mate 的持久语义。只要移动组件已经受已提交 Mate 约束，预览增强就必须让位于
 * 既有 Mate 的剩余自由度，否则会把本来可解的多 Mate 组合误报为破坏已提交 Mate。
 *
 * @param input 判断输入。
 * @returns 是否存在约束当前移动组件的已提交 Mate。
 */
function hasCommittedMateConstrainingMovingComponent(input: {
  /** 当前正在求解的原始 Mate。 */
  readonly sourceMate: AssemblyMateDefinition;
  /** 移动组件实例 ID。 */
  readonly movingComponentInstanceId: string;
  /** 本轮参与求解的全部 Mate。 */
  readonly mateDefinitions: readonly AssemblyMateDefinition[];
  /** PropertyManager 中尚未提交的临时 Mate ID。 */
  readonly transientMateIds: ReadonlySet<string>;
}): boolean {
  return input.mateDefinitions.some((candidateMate) => (
    candidateMate.mateId !== input.sourceMate.mateId
    && !candidateMate.suppressed
    && !input.transientMateIds.has(candidateMate.mateId)
    && resolveAssemblyMateComponentInstanceIds(candidateMate).includes(input.movingComponentInstanceId)
  ));
}

/**
 * 判断临时 Coincident 预览是否允许使用代表点贴合增强。
 *
 * @param input 判断输入。
 * @returns 是否允许使用代表点贴合。
 */
function shouldAlignTransientRepresentativePoints(input: {
  /** 当前求解步 Mate。 */
  readonly mate: AssemblyMateDefinition;
  /** 当前原始 Mate。 */
  readonly sourceMate: AssemblyMateDefinition;
  /** 当前移动侧。 */
  readonly side: MovingSideResolution;
  /** 本轮 Mate 列表。 */
  readonly mateDefinitions: readonly AssemblyMateDefinition[];
  /** 临时 Mate ID 集合。 */
  readonly transientMateIds: ReadonlySet<string>;
  /** 当前 Mate 是否为临时 Mate。 */
  readonly isTransientMate: boolean;
  /** 是否允许临时 Mate 使用初始姿态增强。 */
  readonly useTransientInitialPlacement: boolean;
}): boolean {
  return input.isTransientMate
    && input.useTransientInitialPlacement
    && input.mate.mateType === AssemblyMateType.Coincident
    && !hasCommittedMateConstrainingMovingComponent({
      sourceMate: input.sourceMate,
      movingComponentInstanceId: input.side.movingComponent.componentInstanceId,
      mateDefinitions: input.mateDefinitions,
      transientMateIds: input.transientMateIds,
    });
}

/**
 * 解析 Mate 的无方向组件对 key。
 *
 * @param mate Mate Definition。
 * @returns 组件对 key。
 */
function resolveConcentricComponentPairKey(mate: AssemblyMateDefinition): string {
  return [...resolveAssemblyMateComponentInstanceIds(mate)].sort().join('::');
}

/**
 * 判断 Mate 是否为可参与精确组合求解的普通同轴心。
 *
 * @param mate Mate Definition。
 * @returns 是否为普通同轴心。
 */
function isPlainConcentricMate(mate: AssemblyMateDefinition): boolean {
  return mate.mateType === AssemblyMateType.Concentric
    && !mate.lockRotation
    && !mate.concentricMisalignment
    && !mate.suppressed;
}

/**
 * 为同一组件对的另一条 Mate 按当前移动侧重建移动侧。
 *
 * @param mate 当前求解步中的 peer Mate。
 * @param side 当前 Mate 的移动侧。
 * @param components 当前组件状态。
 * @returns peer Mate 的移动侧；组件对不匹配时返回 null。
 */
function resolvePeerConcentricMovingSide(
  mate: AssemblyMateDefinition,
  side: MovingSideResolution,
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
): MovingSideResolution | null {
  const fixedComponentId = side.fixedComponent.componentInstanceId;
  const movingComponentId = side.movingComponent.componentInstanceId;
  const firstComponent = components[mate.firstEntity.componentInstanceId];
  const secondComponent = components[mate.secondEntity.componentInstanceId];
  if (!firstComponent || !secondComponent) {
    return null;
  }

  if (
    mate.firstEntity.componentInstanceId === fixedComponentId
    && mate.secondEntity.componentInstanceId === movingComponentId
  ) {
    return {
      fixedEntity: mate.firstEntity,
      movingEntity: mate.secondEntity,
      fixedComponent: firstComponent,
      movingComponent: secondComponent,
      movingFirst: false,
    };
  }

  if (
    mate.firstEntity.componentInstanceId === movingComponentId
    && mate.secondEntity.componentInstanceId === fixedComponentId
  ) {
    return {
      fixedEntity: mate.secondEntity,
      movingEntity: mate.firstEntity,
      fixedComponent: secondComponent,
      movingComponent: firstComponent,
      movingFirst: true,
    };
  }

  return null;
}

/**
 * 判断当前 Mate 是否应作为双同轴心组合求解的基准。
 *
 * @param currentMate 当前 Mate。
 * @param peerMate 同组件对的另一条同轴心 Mate。
 * @param transientMateIds 临时 Mate ID 集合。
 * @returns 当前 Mate 是否作为基准。
 */
function shouldUseCurrentConcentricMateAsPairBase(
  currentMate: AssemblyMateDefinition,
  peerMate: AssemblyMateDefinition,
  transientMateIds: ReadonlySet<string>,
): boolean {
  const currentTransient = transientMateIds.has(currentMate.mateId);
  const peerTransient = transientMateIds.has(peerMate.mateId);
  if (currentTransient !== peerTransient) {
    return !currentTransient;
  }
  if (currentMate.createdAt !== peerMate.createdAt) {
    return currentMate.createdAt < peerMate.createdAt;
  }
  return currentMate.mateId.localeCompare(peerMate.mateId) <= 0;
}

/**
 * 在同一组件对存在另一条普通同轴心时，尝试执行组合求解。
 *
 * @param input 组合求解上下文。
 * @returns 组合求解结果；没有可组合 peer 或当前组合不支持时返回 null。
 */
function resolveConcentricPairApplyResult(input: {
  /** 当前求解步 Mate。 */
  readonly mate: AssemblyMateDefinition;
  /** 当前原始 Mate。 */
  readonly sourceMate: AssemblyMateDefinition;
  /** 当前 Mate 的移动侧。 */
  readonly side: MovingSideResolution;
  /** 当前组件状态。 */
  readonly components: Readonly<Record<string, AssemblyMateSolverComponentState>>;
  /** 当前组件累计 world delta。 */
  readonly componentDeltaMap: ReturnType<typeof createComponentWorldDeltaMap>;
  /** 本轮 Mate 列表。 */
  readonly mateDefinitions: readonly AssemblyMateDefinition[];
  /** 临时 Mate ID 集合。 */
  readonly transientMateIds: ReadonlySet<string>;
  /** 求解容差。 */
  readonly epsilon: number;
}): ApplyMateResult | null {
  if (!isPlainConcentricMate(input.sourceMate)) {
    return null;
  }

  const componentPairKey = resolveConcentricComponentPairKey(input.sourceMate);
  const peerSourceMate = input.mateDefinitions.find((candidateMate) => (
    candidateMate.mateId !== input.sourceMate.mateId
    && isPlainConcentricMate(candidateMate)
    && resolveConcentricComponentPairKey(candidateMate) === componentPairKey
  ));
  if (!peerSourceMate) {
    return null;
  }

  const peerMate = transformMateDefinitionForSolve(
    peerSourceMate,
    input.componentDeltaMap,
    input.components,
  );
  const peerSide = resolvePeerConcentricMovingSide(peerMate, input.side, input.components);
  if (!peerSide) {
    return null;
  }

  const currentIsBase = shouldUseCurrentConcentricMateAsPairBase(
    input.sourceMate,
    peerSourceMate,
    input.transientMateIds,
  );
  return currentIsBase
    ? solveConcentricMatePair(input.mate, input.side, peerMate, peerSide, input.epsilon)
    : solveConcentricMatePair(peerMate, peerSide, input.mate, input.side, input.epsilon);
}

/**
 * 判断两条方向是否平行或反向平行。
 *
 * @param first 第一条方向。
 * @param second 第二条方向。
 * @param epsilon 数值容差。
 * @returns 是否平行。
 */
function areDirectionsParallel(
  first: THREE.Vector3,
  second: THREE.Vector3,
  epsilon: number,
): boolean {
  const firstDirection = normalizeVector(first, epsilon);
  const secondDirection = normalizeVector(second, epsilon);
  if (!firstDirection || !secondDirection) {
    return false;
  }
  return firstDirection.clone().cross(secondDirection).length() <= Math.max(epsilon, 1e-6);
}

/**
 * 计算两条平行轴线之间的径向间距。
 *
 * @param firstOrigin 第一条轴线原点。
 * @param secondOrigin 第二条轴线原点。
 * @param axisDirection 公共轴方向。
 * @returns 轴线径向间距。
 */
function calculateParallelAxisRadialOffset(
  firstOrigin: THREE.Vector3,
  secondOrigin: THREE.Vector3,
  axisDirection: THREE.Vector3,
): number {
  const offset = secondOrigin.clone().sub(firstOrigin);
  const axialOffset = axisDirection.clone().multiplyScalar(offset.dot(axisDirection));
  return offset.sub(axialOffset).length();
}

/**
 * 从两条同轴心 Mate 的当前几何中解析公共滑移轴。
 *
 * 设计说明：
 * 1. 两条普通同轴心约束同一组件对且孔轴彼此平行时，SOLIDWORKS 会保留沿公共轴的平移自由度；
 * 2. 该自由度属于已提交 Mate 图的底层约束结果，后续临时 Coincident 必须优先在该自由度内找解；
 * 3. 这里要求两条轴线在固定侧和移动侧都形成非退化间距，避免把重复选择同一孔误判成双孔约束。
 *
 * @param input 同轴心 pair 解析输入。
 * @returns 公共滑移轴方向；无法稳定解析时返回 null。
 */
function resolveConcentricPairTranslationAxis(input: {
  /** 第一条同轴心的当前移动侧。 */
  readonly firstSide: MovingSideResolution;
  /** 第二条同轴心的当前移动侧。 */
  readonly secondSide: MovingSideResolution;
  /** 数值容差。 */
  readonly epsilon: number;
}): THREE.Vector3 | null {
  const firstFixedAxis = resolveAxisGeometry(input.firstSide.fixedEntity);
  const firstMovingAxis = resolveAxisGeometry(input.firstSide.movingEntity);
  const secondFixedAxis = resolveAxisGeometry(input.secondSide.fixedEntity);
  const secondMovingAxis = resolveAxisGeometry(input.secondSide.movingEntity);
  if (!firstFixedAxis || !firstMovingAxis || !secondFixedAxis || !secondMovingAxis) {
    return null;
  }

  const axisDirection = normalizeVector(firstFixedAxis.direction, input.epsilon);
  if (
    !axisDirection
    || !areDirectionsParallel(axisDirection, firstMovingAxis.direction, input.epsilon)
    || !areDirectionsParallel(axisDirection, secondFixedAxis.direction, input.epsilon)
    || !areDirectionsParallel(axisDirection, secondMovingAxis.direction, input.epsilon)
  ) {
    return null;
  }

  if (
    calculateParallelAxisRadialOffset(firstFixedAxis.origin, secondFixedAxis.origin, axisDirection) <= input.epsilon
    || calculateParallelAxisRadialOffset(firstMovingAxis.origin, secondMovingAxis.origin, axisDirection) <= input.epsilon
  ) {
    return null;
  }

  return axisDirection;
}

/**
 * 为受双同轴心约束的临时 Coincident 解析公共轴滑移自由度。
 *
 * @param input 当前求解上下文。
 * @returns 公共轴方向；当前 Coincident 不受双同轴心约束时返回 null。
 */
function resolveConcentricPairTranslationAxisForCoincident(input: {
  /** 当前求解的原始 Mate。 */
  readonly sourceMate: AssemblyMateDefinition;
  /** 当前移动侧。 */
  readonly side: MovingSideResolution;
  /** 当前组件状态。 */
  readonly components: Readonly<Record<string, AssemblyMateSolverComponentState>>;
  /** 当前组件累计 world delta。 */
  readonly componentDeltaMap: ReturnType<typeof createComponentWorldDeltaMap>;
  /** 当前求解图中的全部 Mate。 */
  readonly mateDefinitions: readonly AssemblyMateDefinition[];
  /** 临时 Mate ID 集合。 */
  readonly transientMateIds: ReadonlySet<string>;
  /** 数值容差。 */
  readonly epsilon: number;
}): THREE.Vector3 | null {
  if (input.sourceMate.mateType !== AssemblyMateType.Coincident) {
    return null;
  }

  const componentPairKey = [
    input.side.fixedComponent.componentInstanceId,
    input.side.movingComponent.componentInstanceId,
  ].sort().join('::');
  const committedConcentricMates = input.mateDefinitions.filter((candidateMate) => (
    !input.transientMateIds.has(candidateMate.mateId)
    && isPlainConcentricMate(candidateMate)
    && resolveConcentricComponentPairKey(candidateMate) === componentPairKey
  ));

  for (let firstIndex = 0; firstIndex < committedConcentricMates.length - 1; firstIndex += 1) {
    for (let secondIndex = firstIndex + 1; secondIndex < committedConcentricMates.length; secondIndex += 1) {
      const firstMate = transformMateDefinitionForSolve(
        committedConcentricMates[firstIndex]!,
        input.componentDeltaMap,
        input.components,
      );
      const secondMate = transformMateDefinitionForSolve(
        committedConcentricMates[secondIndex]!,
        input.componentDeltaMap,
        input.components,
      );
      const firstSide = resolvePeerConcentricMovingSide(firstMate, input.side, input.components);
      const secondSide = resolvePeerConcentricMovingSide(secondMate, input.side, input.components);
      if (!firstSide || !secondSide) {
        continue;
      }

      const translationAxis = resolveConcentricPairTranslationAxis({
        firstSide,
        secondSide,
        epsilon: input.epsilon,
      });
      if (translationAxis) {
        return translationAxis;
      }
    }
  }

  return null;
}

/**
 * 根据 local transform 变更同步推导组件 world transform。
 *
 * 设计说明：
 * 求解器内部会多轮更新组件 local transform。若运行时同时提供了 world transform，
 * 几何描述符变换会优先使用 world 基线；因此每次 local 求解后必须同步更新 worldTransform，
 * 否则下一轮校验会把已移动组件仍看作选择瞬间位置，造成普通预览被误判为 unsolved / overDefined。
 *
 * @param currentWorldTransform 当前 world transform；缺失时返回 undefined。
 * @param previousTransform 本轮移动前 local transform。
 * @param nextTransform 本轮移动后 local transform。
 * @returns 更新后的 world transform。
 */
function updateWorldTransformFromLocalDelta(
  currentWorldTransform: ComponentTransformSnapshot | undefined,
  previousTransform: ComponentTransformSnapshot,
  nextTransform: ComponentTransformSnapshot,
): ComponentTransformSnapshot | undefined {
  if (!currentWorldTransform) {
    return undefined;
  }

  const previousMatrix = composeMatrix(previousTransform);
  const nextMatrix = composeMatrix(nextTransform);
  const currentWorldMatrix = composeMatrix(currentWorldTransform);
  const parentWorldMatrix = currentWorldMatrix.clone().multiply(previousMatrix.clone().invert());
  return {
    ...decomposeMatrix(parentWorldMatrix.multiply(nextMatrix)),
    // 刚体位移不改变世界缩放；沿用原缩放，避免 decompose 累积缩放漂移。
    scale: [...currentWorldTransform.scale] as readonly [number, number, number],
  };
}

/**
 * 创建两个世界 transform 之间的 delta。
 *
 * @param previousWorldTransform 变更前世界 transform。
 * @param nextWorldTransform 变更后世界 transform。
 * @returns 从旧世界位姿到新世界位姿的 delta。
 */
function createWorldDeltaBetweenTransforms(
  previousWorldTransform: ComponentTransformSnapshot,
  nextWorldTransform: ComponentTransformSnapshot,
): THREE.Matrix4 {
  const previousMatrix = composeMatrix(previousWorldTransform);
  const nextMatrix = composeMatrix(nextWorldTransform);
  return nextMatrix.multiply(previousMatrix.invert());
}

/**
 * 把某个组件在本步产生的世界刚体位移联动写入其所在刚性组的其余成员。
 *
 * 设计说明：
 * 已证明相对自由度为 0 的浮动刚性子块必须作为单一刚体运动。逐条投影只移动 Mate 命中的一侧组件时，
 * 会破坏刚体内部约束并在闭环里往复振荡。这里把主组件的世界 delta 以相同世界刚体变换应用到同组其余成员，
 * 使子块整体平移/旋转，从而把伪闭环退化为可稳定收敛的树。
 *
 * @param input 联动写入输入。
 * @returns 是否有任意成员发生位姿变化。
 */
function coMoveRigidGroupMembers(input: {
  /** 本步实际移动的主组件实例 ID。 */
  readonly primaryComponentInstanceId: string;
  /** 主组件产生的世界刚体 delta。 */
  readonly worldDelta: THREE.Matrix4;
  /** 主组件所在刚性组的全部成员组件实例 ID。 */
  readonly groupMemberIds: readonly string[];
  /** 可变组件状态表。 */
  readonly mutableComponents: Record<string, AssemblyMateSolverComponentState>;
  /** 组件本地 transform 表。 */
  readonly componentTransforms: Record<string, ComponentTransformSnapshot>;
  /** 组件世界 delta 表。 */
  readonly componentDeltaMap: ReturnType<typeof createComponentWorldDeltaMap>;
  /** 求解容差。 */
  readonly epsilon: number;
}): boolean {
  let hasMemberChanged = false;

  input.groupMemberIds.forEach((memberComponentInstanceId) => {
    if (memberComponentInstanceId === input.primaryComponentInstanceId) {
      return;
    }
    const previousTransform = input.componentTransforms[memberComponentInstanceId];
    const previousComponent = input.mutableComponents[memberComponentInstanceId];
    if (!previousTransform || !previousComponent) {
      return;
    }

    const nextTransform = applyWorldDeltaToTransform(
      previousTransform,
      input.worldDelta,
      previousComponent.worldTransform,
    );
    if (areTransformsClose(previousTransform, nextTransform, input.epsilon)) {
      return;
    }

    input.componentTransforms[memberComponentInstanceId] = nextTransform;
    updateComponentWorldDeltaMap(
      input.componentDeltaMap,
      memberComponentInstanceId,
      previousTransform,
      nextTransform,
    );
    const nextWorldTransform = updateWorldTransformFromLocalDelta(
      previousComponent.worldTransform,
      previousTransform,
      nextTransform,
    );
    const previousEffectiveWorldTransform = previousComponent.worldTransform ?? previousTransform;
    const nextEffectiveWorldTransform = nextWorldTransform ?? nextTransform;
    const nextWorldBounds = previousComponent.worldBounds
      ? transformStandardMateWorldBounds(
          previousComponent.worldBounds,
          createWorldDeltaBetweenTransforms(previousEffectiveWorldTransform, nextEffectiveWorldTransform),
        )
      : undefined;
    input.mutableComponents[memberComponentInstanceId] = {
      ...previousComponent,
      transform: nextTransform,
      ...(nextWorldTransform ? { worldTransform: nextWorldTransform } : {}),
      ...(nextWorldBounds ? { worldBounds: nextWorldBounds } : {}),
    };
    hasMemberChanged = true;
  });

  return hasMemberChanged;
}

/**
 * 读取组件所在刚性组的成员集合。
 *
 * @param rigidGroups 刚性组解析结果。
 * @param componentInstanceId 组件实例 ID。
 * @returns 刚性组成员；组件不在任何刚性组时返回 `null`。
 */
function resolveRigidGroupMemberIds(
  rigidGroups: StandardMateRigidComponentGroups,
  componentInstanceId: string,
): readonly string[] | null {
  const groupRoot = rigidGroups.groupRootByComponentId.get(componentInstanceId);
  if (groupRoot === undefined) {
    return null;
  }
  return rigidGroups.membersByGroupRoot.get(groupRoot) ?? null;
}

/**
 * 把固定状态沿刚性边界传播到整块。
 *
 * 设计说明：
 * 已证明相对自由度为 0 的刚性子块里只要有一个成员被固定，整块就没有任何相对运动余地，物理上等价于整块固定
 * （这正是“fixed 只能通过已证明剩余相对自由度为 0 的刚性边界传播”的要求）。求解阶段必须把这种隐含固定显式化：
 * 否则块内其余成员仍被当作浮动，块内多条同轴心会重新振荡，导致相邻零件被误判为不可解、表现为“整组被完全固定”。
 * 传播只发生在刚性块内部，Concentric / Coincident / 闭环连通等普通图连接不会触发。
 *
 * @param components 原始组件状态表。
 * @param rigidGroups 刚性组解析结果。
 * @returns 已把整块固定显式化的组件状态表；无固定块时返回原表引用。
 */
function propagateFixedAcrossRigidGroups(
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
  rigidGroups: StandardMateRigidComponentGroups,
): Record<string, AssemblyMateSolverComponentState> {
  const fixedMemberIds = new Set<string>();
  rigidGroups.membersByGroupRoot.forEach((memberIds) => {
    if (memberIds.some((memberId) => components[memberId] && isComponentFixed(components[memberId]!))) {
      memberIds.forEach((memberId) => fixedMemberIds.add(memberId));
    }
  });
  if (fixedMemberIds.size === 0) {
    return { ...components };
  }

  const effectiveComponents: Record<string, AssemblyMateSolverComponentState> = { ...components };
  fixedMemberIds.forEach((memberId) => {
    const component = effectiveComponents[memberId];
    if (component && !isComponentFixed(component)) {
      effectiveComponents[memberId] = {
        ...component,
        fixationState: 'fixed',
      };
    }
  });
  return effectiveComponents;
}

/**
 * 读取组件到求解锚点的图距离。
 *
 * @param componentGraphDistances 组件图距离映射。
 * @param componentInstanceId 组件实例 ID。
 * @returns 图距离；不可达时返回正无穷。
 */
function resolveComponentGraphDistance(
  componentGraphDistances: ReadonlyMap<string, number>,
  componentInstanceId: string,
): number {
  return componentGraphDistances.get(componentInstanceId) ?? Number.POSITIVE_INFINITY;
}

/**
 * 解析移动侧。
 *
 * @param mate 配合定义。
 * @param components 组件状态。
 * @param componentGraphDistances 组件到当前求解锚点的图距离。
 * @param stationaryComponentIds 本轮求解中已作为上游约束定位的组件 ID。
 * @returns 移动侧或错误结果。
 */
function resolveMovingSide(
  mate: AssemblyMateDefinition,
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
  componentGraphDistances: ReadonlyMap<string, number>,
  stationaryComponentIds: ReadonlySet<string>,
): MovingSideResolution | ApplyMateResult {
  const referenceDependencyIssue = resolveAssemblyReferenceDependencySolveIssue(mate);
  if (referenceDependencyIssue) {
    return referenceDependencyIssue;
  }

  const firstComponent = components[mate.firstEntity.componentInstanceId];
  const secondComponent = components[mate.secondEntity.componentInstanceId];
  if (!firstComponent || !secondComponent) {
    return {
      status: MateSolveStatus.MissingReference,
      message: '配合引用的组件实例不存在。',
    };
  }
  if (firstComponent.componentInstanceId === secondComponent.componentInstanceId) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '标准配合不能在同一组件实例内部求解。',
    };
  }

  const firstFixed = isComponentFixed(firstComponent);
  const secondFixed = isComponentFixed(secondComponent);
  if (firstFixed && secondFixed) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '两个组件均为固定状态，无法移动任意组件完成配合。',
    };
  }

  if (firstFixed && !secondFixed) {
    return {
      fixedEntity: mate.firstEntity,
      movingEntity: mate.secondEntity,
      fixedComponent: firstComponent,
      movingComponent: secondComponent,
      movingFirst: false,
    };
  }
  if (!firstFixed && secondFixed) {
    return {
      fixedEntity: mate.secondEntity,
      movingEntity: mate.firstEntity,
      fixedComponent: secondComponent,
      movingComponent: firstComponent,
      movingFirst: true,
    };
  }

  const firstGraphDistance = resolveComponentGraphDistance(
    componentGraphDistances,
    firstComponent.componentInstanceId,
  );
  const secondGraphDistance = resolveComponentGraphDistance(
    componentGraphDistances,
    secondComponent.componentInstanceId,
  );
  if (firstGraphDistance !== secondGraphDistance) {
    return firstGraphDistance < secondGraphDistance
      ? {
          fixedEntity: mate.firstEntity,
          movingEntity: mate.secondEntity,
          fixedComponent: firstComponent,
          movingComponent: secondComponent,
          movingFirst: false,
        }
      : {
          fixedEntity: mate.secondEntity,
          movingEntity: mate.firstEntity,
          fixedComponent: secondComponent,
          movingComponent: firstComponent,
          movingFirst: true,
        };
  }

  const firstStationary = stationaryComponentIds.has(firstComponent.componentInstanceId);
  const secondStationary = stationaryComponentIds.has(secondComponent.componentInstanceId);
  if (firstStationary && !secondStationary) {
    return {
      fixedEntity: mate.firstEntity,
      movingEntity: mate.secondEntity,
      fixedComponent: firstComponent,
      movingComponent: secondComponent,
      movingFirst: false,
    };
  }
  if (!firstStationary && secondStationary) {
    return {
      fixedEntity: mate.secondEntity,
      movingEntity: mate.firstEntity,
      fixedComponent: secondComponent,
      movingComponent: firstComponent,
      movingFirst: true,
    };
  }

  // 两个组件都浮动时对齐 SOLIDWORKS 的可预测默认策略：移动第二选择组件。
  return {
    fixedEntity: mate.firstEntity,
    movingEntity: mate.secondEntity,
    fixedComponent: firstComponent,
    movingComponent: secondComponent,
    movingFirst: false,
  };
}

/**
 * 应用方向约束。
 *
 * @param movingTransform 移动组件 transform。
 * @param movingOrigin 移动实体参考点。
 * @param movingDirection 移动实体方向。
 * @param targetDirection 目标方向。
 * @param epsilon 容差。
 * @returns 旋转后 transform。
 */
function applyDirectionAlignment(
  movingTransform: ComponentTransformSnapshot,
  movingOrigin: THREE.Vector3,
  movingDirection: THREE.Vector3,
  targetDirection: THREE.Vector3,
  epsilon: number,
): ComponentTransformSnapshot | null {
  const delta = createRotationDeltaBetweenDirections(
    movingOrigin,
    movingDirection,
    targetDirection,
    epsilon,
  );
  if (!delta) {
    return null;
  }
  return applyWorldDeltaToTransform(movingTransform, delta);
}

/**
 * 应用角度约束。
 *
 * @param movingTransform 移动组件 transform。
 * @param movingOrigin 移动实体参考点。
 * @param movingDirection 移动实体方向。
 * @param targetDirection 目标方向。
 * @param desiredAngle 目标夹角。
 * @param epsilon 容差。
 * @returns 旋转后 transform。
 */
function applyDirectionAngle(
  movingTransform: ComponentTransformSnapshot,
  movingOrigin: THREE.Vector3,
  movingDirection: THREE.Vector3,
  targetDirection: THREE.Vector3,
  desiredAngle: number,
  epsilon: number,
): ComponentTransformSnapshot | null {
  const movingNormalized = normalizeVector(movingDirection, epsilon);
  const targetNormalized = normalizeVector(targetDirection, epsilon);
  if (!movingNormalized || !targetNormalized) {
    return null;
  }
  const currentAngle = movingNormalized.angleTo(targetNormalized);
  const deltaAngle = currentAngle - desiredAngle;
  if (Math.abs(deltaAngle) <= epsilon) {
    return movingTransform;
  }
  let rotationAxis = movingNormalized.clone().cross(targetNormalized);
  if (rotationAxis.lengthSq() <= epsilon) {
    const seed = Math.abs(movingNormalized.x) < 0.9
      ? new THREE.Vector3(1, 0, 0)
      : new THREE.Vector3(0, 1, 0);
    rotationAxis = movingNormalized.clone().cross(seed);
  }
  const normalizedAxis = normalizeVector(rotationAxis, epsilon);
  if (!normalizedAxis) {
    return null;
  }
  const rotation = new THREE.Quaternion().setFromAxisAngle(normalizedAxis, deltaAngle);
  return applyWorldDeltaToTransform(
    movingTransform,
    createRotationDeltaAroundPoint(movingOrigin, rotation),
  );
}

/**
 * 解析角度配合参考实体的旋转轴方向。
 *
 * SOLIDWORKS 的 Angle Reference Entity 可以是轴、边或面。求解器这里只关心
 * “用于定义有符号角度的世界空间方向”，因此轴线使用轴向，平面使用法向。
 *
 * @param referenceEntity 参考实体。
 * @param epsilon 容差。
 * @returns 参考旋转轴方向；不可解析时返回 null。
 */
function resolveAngleReferenceDirection(
  referenceEntity: MateEntityRef | undefined,
  epsilon: number,
): THREE.Vector3 | null {
  if (!referenceEntity) {
    return null;
  }

  const referenceAxis = resolveAxisGeometry(referenceEntity);
  if (referenceAxis) {
    return normalizeVector(referenceAxis.direction, epsilon);
  }

  const referencePlane = resolvePlaneGeometry(referenceEntity);
  if (referencePlane) {
    return normalizeVector(referencePlane.normal, epsilon);
  }

  return null;
}

/**
 * 判断角度配合参考轴是否与两条角度实体方向垂直。
 *
 * 参考轴不是任意的“旋转提示”，它必须能定义两个实体之间的有符号角度；
 * 如果不垂直，绕该轴旋转无法稳定保持角度，继续求解会制造看似可用但会翻转的结果。
 *
 * @param referenceDirection 参考轴方向。
 * @param targetDirection 固定侧方向。
 * @param movingDirection 移动侧方向。
 * @param epsilon 容差。
 * @returns 是否可用于有符号角度求解。
 */
function isAngleReferenceDirectionCompatible(
  referenceDirection: THREE.Vector3,
  targetDirection: THREE.Vector3,
  movingDirection: THREE.Vector3,
  epsilon: number,
): boolean {
  const referenceNormalized = normalizeVector(referenceDirection, epsilon);
  const targetNormalized = normalizeVector(targetDirection, epsilon);
  const movingNormalized = normalizeVector(movingDirection, epsilon);
  if (!referenceNormalized || !targetNormalized || !movingNormalized) {
    return false;
  }

  const perpendicularTolerance = Math.max(epsilon * 10, 1e-5);
  return Math.abs(referenceNormalized.dot(targetNormalized)) <= perpendicularTolerance
    && Math.abs(referenceNormalized.dot(movingNormalized)) <= perpendicularTolerance;
}

/**
 * 判断方向类配合是否为平面与轴线组合。
 *
 * @param targetPlane 固定侧平面几何。
 * @param movingPlane 移动侧平面几何。
 * @param targetAxis 固定侧轴线几何。
 * @param movingAxis 移动侧轴线几何。
 * @returns 是否为一个平面和一条轴线的组合。
 */
function isPlaneAxisDirectionalPair(
  targetPlane: ReturnType<typeof resolvePlaneGeometry>,
  movingPlane: ReturnType<typeof resolvePlaneGeometry>,
  targetAxis: ReturnType<typeof resolveAxisGeometry>,
  movingAxis: ReturnType<typeof resolveAxisGeometry>,
): boolean {
  return Boolean((targetPlane && movingAxis) || (targetAxis && movingPlane));
}

/**
 * 解析 SOLIDWORKS 方向类配合实际使用的方向夹角。
 *
 * 设计说明：
 * 平面-线/轴不是两个普通方向向量之间的关系。SOLIDWORKS 中“线平行平面”表示
 * 线方向落在平面内，也就是线方向与平面法向垂直；“线垂直平面”表示线方向与
 * 平面法向平行。因此平面-轴线组合需要在求解器边界统一映射，避免 UI 或业务层
 * 自行修正几何语义。
 *
 * @param mate 当前配合。
 * @param desiredAngle 默认方向夹角。
 * @param isPlaneAxisPair 是否为平面与轴线组合。
 * @returns 实际求解夹角。
 */
function resolveDirectionalMateDesiredAngle(
  mate: AssemblyMateDefinition,
  desiredAngle: number,
  isPlaneAxisPair: boolean,
): number {
  if (!isPlaneAxisPair) {
    return desiredAngle;
  }

  if (mate.mateType === AssemblyMateType.Parallel) {
    return Math.PI / 2;
  }

  if (mate.mateType === AssemblyMateType.Perpendicular) {
    return 0;
  }

  return desiredAngle;
}

/**
 * 使用参考实体求解有符号角度。
 *
 * 设计说明：
 * SOLIDWORKS 在 Angle Mate 中加入 Reference Entity 后，参考实体定义旋转轴，Flip Dimension
 * 和维度选择器共同决定角度落在轴的哪一侧。当前实现把 Flip Dimension 映射为参考轴上的
 * 有符号目标角度，从而避免拖动后在两个镜像解之间跳变。
 *
 * @param movingTransform 移动组件 transform。
 * @param movingOrigin 移动实体参考点。
 * @param movingDirection 移动实体方向。
 * @param targetDirection 固定侧目标方向。
 * @param referenceDirection 参考旋转轴方向。
 * @param desiredAngle 目标角度。
 * @param flipped 是否反转尺寸方向。
 * @param epsilon 容差。
 * @returns 旋转后 transform。
 */
function applyReferencedDirectionAngle(
  movingTransform: ComponentTransformSnapshot,
  movingOrigin: THREE.Vector3,
  movingDirection: THREE.Vector3,
  targetDirection: THREE.Vector3,
  referenceDirection: THREE.Vector3,
  desiredAngle: number,
  flipped: boolean,
  epsilon: number,
): ComponentTransformSnapshot | null {
  const movingNormalized = normalizeVector(movingDirection, epsilon);
  const targetNormalized = normalizeVector(targetDirection, epsilon);
  const referenceNormalized = normalizeVector(referenceDirection, epsilon);
  if (!movingNormalized || !targetNormalized || !referenceNormalized) {
    return null;
  }

  const signedNumerator = referenceNormalized.dot(targetNormalized.clone().cross(movingNormalized));
  const signedDenominator = THREE.MathUtils.clamp(targetNormalized.dot(movingNormalized), -1, 1);
  const currentSignedAngle = Math.atan2(signedNumerator, signedDenominator);
  const desiredSignedAngle = flipped ? -desiredAngle : desiredAngle;
  const deltaAngle = desiredSignedAngle - currentSignedAngle;
  if (Math.abs(deltaAngle) <= epsilon) {
    return movingTransform;
  }

  const rotation = new THREE.Quaternion().setFromAxisAngle(referenceNormalized, deltaAngle);
  return applyWorldDeltaToTransform(
    movingTransform,
    createRotationDeltaAroundPoint(movingOrigin, rotation),
  );
}

/**
 * 求解方向类配合。
 *
 * @param mate 配合定义。
 * @param side 移动侧。
 * @param desiredAngle 目标夹角。
 * @param epsilon 容差。
 * @returns 应用结果。
 */
function solveDirectionalMate(
  mate: AssemblyMateDefinition,
  side: MovingSideResolution,
  desiredAngle: number,
  epsilon: number,
): ApplyMateResult {
  const targetPlane = resolvePlaneGeometry(side.fixedEntity);
  const movingPlane = resolvePlaneGeometry(side.movingEntity);
  const targetAxis = resolveAxisGeometry(side.fixedEntity);
  const movingAxis = resolveAxisGeometry(side.movingEntity);

  const targetDirection = targetPlane?.normal ?? targetAxis?.direction ?? null;
  const movingDirection = movingPlane?.normal ?? movingAxis?.direction ?? null;
  const movingOrigin = movingPlane?.point ?? movingAxis?.origin ?? null;
  const effectiveDesiredAngle = resolveDirectionalMateDesiredAngle(
    mate,
    desiredAngle,
    isPlaneAxisDirectionalPair(targetPlane, movingPlane, targetAxis, movingAxis),
  );
  if (!targetDirection || !movingDirection || !movingOrigin) {
    return {
      status: MateSolveStatus.UnsupportedGeometry,
      message: '当前实体缺少可用于方向约束的法向或轴向。',
    };
  }

  const alignedTargetDirection = mate.alignment === MateAlignment.AntiAligned
    ? targetDirection.clone().multiplyScalar(-1)
    : targetDirection;
  const angleReferenceDirection = mate.mateType === AssemblyMateType.Angle
    ? resolveAngleReferenceDirection(mate.referenceEntity, epsilon)
    : null;
  if (mate.mateType === AssemblyMateType.Angle && mate.referenceEntity && !angleReferenceDirection) {
    return {
      status: MateSolveStatus.UnsupportedGeometry,
      message: '角度配合参考实体缺少可用于定义旋转轴的轴向或法向。',
    };
  }

  if (
    mate.mateType === AssemblyMateType.Angle
    && angleReferenceDirection
    && !isAngleReferenceDirectionCompatible(angleReferenceDirection, alignedTargetDirection, movingDirection, epsilon)
  ) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '角度配合参考实体必须垂直于两个角度配合实体。',
    };
  }

  const nextTransform = mate.mateType === AssemblyMateType.Angle && angleReferenceDirection
    ? applyReferencedDirectionAngle(
        side.movingComponent.transform,
        movingOrigin,
        movingDirection,
        alignedTargetDirection,
        angleReferenceDirection,
        effectiveDesiredAngle,
        mate.dimension?.flipped ?? false,
        epsilon,
      )
    : effectiveDesiredAngle <= epsilon || Math.abs(effectiveDesiredAngle - Math.PI) <= epsilon
      ? applyDirectionAlignment(side.movingComponent.transform, movingOrigin, movingDirection, alignedTargetDirection, epsilon)
      : applyDirectionAngle(side.movingComponent.transform, movingOrigin, movingDirection, alignedTargetDirection, effectiveDesiredAngle, epsilon);

  if (!nextTransform) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '方向约束求解失败。',
    };
  }

  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: side.movingComponent.componentInstanceId,
    transform: nextTransform,
  };
}

/**
 * 求解平面重合或距离。
 *
 * @param mate 配合定义。
 * @param side 移动侧。
 * @param distance 目标距离。
 * @param epsilon 容差。
 * @param alignGeometry 是否需要先对齐几何方向。
 * @param useAxisDistanceReference 是否使用圆形轴线中心参考点计算距离。
 * @returns 应用结果。
 */
/**
 * 应用单条配合。
 *
 * @param mate 当前求解步配合定义。
 * @param sourceMate 创建 Mate 时的原始配合定义。
 * @param components 当前组件状态。
 * @param componentDeltaMap 当前组件累计 world delta。
 * @param epsilon 数值容差。
 * @param componentGraphDistances 组件到当前求解锚点的图距离。
 * @param stationaryComponentIds 本轮求解中已作为上游约束定位的组件 ID。
 * @param linearDimensionScale 线性尺寸换算比例。
 * @param distanceBranchPolicy 距离符号分支策略。
 * @param mateDefinitions 本轮 Mate 列表。
 * @param transientMateIds 临时 Mate ID 集合。
 * @param isTransientMate 当前 Mate 是否为临时 Mate。
 * @param useTransientInitialPlacement 是否允许临时 Mate 执行初始姿态增强。
 * @returns 应用结果。
 */
function applyMateDefinition(
  mate: AssemblyMateDefinition,
  sourceMate: AssemblyMateDefinition,
  mateById: ReadonlyMap<string, AssemblyMateDefinition>,
  concentricMisalignmentSolvePolicies: ReadonlyMap<string, ConcentricMisalignmentSolvePolicy>,
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
  componentDeltaMap: ReturnType<typeof createComponentWorldDeltaMap>,
  epsilon: number,
  componentGraphDistances: ReadonlyMap<string, number>,
  stationaryComponentIds: ReadonlySet<string>,
  linearDimensionScale: number,
  distanceBranchPolicy: StandardMateDistanceBranchPolicy,
  mateDefinitions: readonly AssemblyMateDefinition[],
  transientMateIds: ReadonlySet<string>,
  isTransientMate: boolean,
  useTransientInitialPlacement: boolean,
): ApplyMateResult {
  if (mate.suppressed) {
    return {
      status: MateSolveStatus.Solved,
      message: '配合已抑制，跳过求解。',
    };
  }

  const side = resolveMovingSide(mate, components, componentGraphDistances, stationaryComponentIds);
  if ('status' in side) {
    return side;
  }

  if (mate.mateType === AssemblyMateType.Lock) {
    return solveLockMate(mate, side);
  }
  if (mate.mateType === AssemblyMateType.Concentric) {
    const misalignmentPolicy = concentricMisalignmentSolvePolicies.get(mate.mateId);
    if (misalignmentPolicy?.skipSolve) {
      return {
        status: MateSolveStatus.Solved,
        message: '同轴心错位配合由链接 Mate 分配偏差，当前 Mate 仅参与最终偏差校验。',
      };
    }
    if (misalignmentPolicy?.symmetricLinkedMateId) {
      const linkedMate = mateById.get(misalignmentPolicy.symmetricLinkedMateId);
      if (!linkedMate) {
        return {
          status: MateSolveStatus.MissingReference,
          message: '同轴心错位对称分摊缺少 linked Mate。',
        };
      }
      const linkedSide = resolveMovingSide(linkedMate, components, componentGraphDistances, stationaryComponentIds);
      if ('status' in linkedSide) {
        return linkedSide;
      }
      return solveSymmetricMisalignedConcentricMate(mate, linkedMate, side, linkedSide, epsilon);
    }
    const pairResult = resolveConcentricPairApplyResult({
      mate,
      sourceMate,
      side,
      components,
      componentDeltaMap,
      mateDefinitions,
      transientMateIds,
      epsilon,
    });
    if (pairResult) {
      return pairResult;
    }
    return solveConcentricMate(mate, side, epsilon, {
      useInitialPlacementBounds: isTransientMate && useTransientInitialPlacement,
      // 已提交圆柱孔同轴心按最小旋转保持朝向，避免拖拽扰动时按 alignment 强推方向导致刚体 180° 翻转；
      // 瞬态预览（新建/切换对齐）仍尊重同向/反向选择。
      preserveRoundHoleOrientation: !isTransientMate,
    });
  }
  if (mate.mateType === AssemblyMateType.Tangent) {
    return solveTangentMate(mate, side, epsilon);
  }
  if (mate.mateType === AssemblyMateType.Parallel) {
    return solveDirectionalMate(mate, side, 0, epsilon);
  }
  if (mate.mateType === AssemblyMateType.Perpendicular) {
    return solveDirectionalMate(mate, side, Math.PI / 2, epsilon);
  }
  if (mate.mateType === AssemblyMateType.Angle) {
    return solveDirectionalMate(mate, side, mate.dimension?.value ?? 0, epsilon);
  }

  if (mate.mateType === AssemblyMateType.Coincident || mate.mateType === AssemblyMateType.Distance) {
    const distances = resolveDistanceOffsetsForBranchPolicy(
      mate,
      sourceMate,
      side,
      linearDimensionScale,
      distanceBranchPolicy,
      epsilon,
    );
    if (distances.length === 0) {
      return {
        status: MateSolveStatus.UnsupportedGeometry,
        message: '当前距离配合条件暂不支持求解。',
      };
    }

    const constrainedTranslationAxis = resolveConcentricPairTranslationAxisForCoincident({
      sourceMate,
      side,
      components,
      componentDeltaMap,
      mateDefinitions,
      transientMateIds,
      epsilon,
    });
    const results = distances.map((distance) => (
      solveCoincidentOrDistanceOffset(
        mate,
        side,
        distance,
        epsilon,
        shouldAlignTransientRepresentativePoints({
          mate,
          sourceMate,
          side,
          mateDefinitions,
          transientMateIds,
          isTransientMate,
          useTransientInitialPlacement,
        }),
        constrainedTranslationAxis,
      )
    ));
    if (
      mate.mateType === AssemblyMateType.Distance
      && distanceBranchPolicy === StandardMateDistanceBranchPolicy.Continuous
      && results.length > 1
    ) {
      return selectContinuousDistanceResult(side.movingComponent.transform, results);
    }
    return results[0] ?? {
      status: MateSolveStatus.UnsupportedGeometry,
      message: '当前标准配合的几何组合暂不支持求解。',
    };
  }

  return {
    status: MateSolveStatus.UnsupportedGeometry,
    message: '当前标准配合的几何组合暂不支持求解。',
  };
}

/**
 * 标准配合求解器。
 *
 * 设计说明：
 * 1. 该类是无状态门面，只为了给业务层提供清晰的求解边界；
 * 2. 具体计算保持在文件内纯函数中，方便单元测试直接覆盖；
 * 3. 后续替换为更完整的约束图求解器时，业务层仍只依赖本门面。
 */
export class StandardMateSolver {
  /**
   * 求解标准配合。
   *
   * @param input 求解输入。
   * @returns 求解结果。
   */
  public solve(input: StandardMateSolverInput): StandardMateSolverResult {
    const debugTrace = createStandardMateSolverDebugTraceSession(input);
    const epsilon = input.epsilon ?? DEFAULT_SOLVER_EPSILON;
    const componentTransforms = cloneComponentTransforms(input.components);
    const transientMateIds = new Set(input.transientMateIds ?? []);
    const useTransientInitialPlacement = input.useTransientInitialPlacement ?? true;
    const sourceMateDefinitions = input.targetMateId
      ? input.mateDefinitions.filter((mate) => mate.mateId === input.targetMateId)
      : input.mateDefinitions;

    const currentResidualResult = resolveStandardMateCurrentResidualSolve(input, debugTrace);
    if (currentResidualResult) {
      guardStandardMateRigidInvariants(input.components, currentResidualResult.componentTransforms);
      emitStandardMateSolverDebugTrace(debugTrace, currentResidualResult);
      return currentResidualResult;
    }

    const componentDeltaMap = createComponentWorldDeltaMap(input.components);
    const drivenComponentIds = new Set(input.drivenComponentInstanceIds ?? []);
    const previewAnchorComponentIds = new Set(input.previewAnchorComponentInstanceIds ?? []);

    /**
     * 预解析当前配合图中的浮动刚性组。
     *
     * 已证明相对自由度为 0 的刚性子块（例如两两之间 2 条独立同轴心 + 重合的电机与连杆）若不被当作单一刚体，
     * 相邻零件对它的多条同轴心会在逐条投影里构成伪闭环并振荡不收敛。这里把这类刚性块内部约束跳过、成员联动位移，
     * 使其作为单一刚体运动，从而把伪闭环退化为可收敛的树。所有刚性块都参与归并（含固定/驱动成员的块也一样），
     * 含固定成员的块随后通过固定传播把整块显式化为固定，避免块内多条同轴心在投影里重新振荡。
     *
     * 刚性判定只能基于“已提交配合”：PropertyManager 预览中的草稿（transient）Mate 尚未提交，不能贡献刚性。
     * 否则新增的重合草稿会被当成轴向锁定平面，把本来还保留轴向平移自由度的两条平行同轴心误判成刚体，进而把浮动
     * 组件误传播为固定，导致这条重合草稿永远滑不到位、被误报“重合无法满足”。
     */
    const committedCurrentMateDefinitions = sourceMateDefinitions
      .filter((mate) => !transientMateIds.has(mate.mateId))
      .map((mate) => transformMateDefinitionForSolve(mate, componentDeltaMap, input.components));
    const rigidGroups = resolveStandardMateRigidComponentGroups(
      input,
      committedCurrentMateDefinitions,
      resolveStandardMateConstraintTolerance({ epsilon: input.epsilon, angleEpsilon: input.angleEpsilon }),
    );

    // 沿刚性边界把固定状态传播到整块，作为后续求解计划与投影阶段使用的有效组件状态。
    const effectiveComponents = propagateFixedAcrossRigidGroups(input.components, rigidGroups);

    /**
     * 驱动组件所在的刚性块整块作为驱动锚点。
     *
     * 用户直接驱动刚性块内任一零件时，整块作为单一刚体一起运动，块内其余成员的位姿由驱动件决定，不应被外部约束
     * 单独拉回。这里把驱动件所在块的全部成员并入驱动锚点集合，使求解计划把外部约束的“移动侧”落在外部零件上，
     * 而不是在图距离相等时误选块内成员——否则联动位移会把整块（含驱动锚点）带偏，导致同一块因选中成员不同而时解时不解。
     */
    const anchoredDrivenComponentIds = new Set<string>(drivenComponentIds);
    rigidGroups.membersByGroupRoot.forEach((memberIds) => {
      if (memberIds.some((memberId) => drivenComponentIds.has(memberId))) {
        memberIds.forEach((memberId) => anchoredDrivenComponentIds.add(memberId));
      }
    });

    const mutableComponents: Record<string, AssemblyMateSolverComponentState> = { ...effectiveComponents };
    const solvePlan = createStandardMateSolvePlan({
      mateDefinitions: sourceMateDefinitions,
      components: effectiveComponents,
      drivenComponentIds: anchoredDrivenComponentIds,
      previewAnchorComponentIds,
    });
    const mateDefinitions = solvePlan.mateDefinitions;
    const concentricMisalignmentSolvePolicies = createConcentricMisalignmentSolvePolicyMap(mateDefinitions);
    const linearDimensionScale = resolveStandardMateLinearDimensionScale(input.linearDimensionScale);
    const distanceBranchPolicy = input.distanceBranchPolicy
      ?? StandardMateDistanceBranchPolicy.SelectionOrder;

    let diagnostics: AssemblyMateSolveDiagnostic[] = [];

    appendStandardMateSolverDebugTraceEvent(debugTrace, 'projection.solvePlan', {
      sourceMateIds: sourceMateDefinitions.map((mate) => mate.mateId),
      orderedMateIds: mateDefinitions.map((mate) => mate.mateId),
      initialStationaryComponentIds: solvePlan.initialStationaryComponentIds,
      componentGraphDistances: Object.fromEntries([...solvePlan.componentGraphDistances.entries()]),
      drivenComponentInstanceIds: [...drivenComponentIds],
      previewAnchorComponentInstanceIds: [...previewAnchorComponentIds],
      distanceBranchPolicy,
      useTransientInitialPlacement,
    });

    for (let iterationIndex = 0; iterationIndex < DEFAULT_SOLVER_MAX_ITERATIONS; iterationIndex += 1) {
      const iterationDiagnostics: AssemblyMateSolveDiagnostic[] = [];
      const stationaryComponentIds = new Set([
        ...solvePlan.initialStationaryComponentIds,
      ]);
      let hasTransformChanged = false;

      mateDefinitions.forEach((mate) => {
        // 刚性组内部约束由联动刚体运动保证满足；冗余共线同轴心会与已保留同轴心互相拉扯形成极限环。
        // 两类 Mate 都在投影阶段跳过，仅参与最终残差校验，避免破坏刚体或引入伪冲突。
        if (rigidGroups.internalMateIds.has(mate.mateId) || rigidGroups.redundantMateIds.has(mate.mateId)) {
          iterationDiagnostics.push({
            mateId: mate.mateId,
            status: MateSolveStatus.Solved,
            message: undefined,
            movedComponentInstanceId: undefined,
          });
          return;
        }
        const mateForCurrentStep = transformMateDefinitionForSolve(mate, componentDeltaMap, mutableComponents);
        const misalignmentPolicy = concentricMisalignmentSolvePolicies.get(mate.mateId);
        const mateById = new Map<string, AssemblyMateDefinition>();
        if (misalignmentPolicy?.symmetricLinkedMateId) {
          mateDefinitions.forEach((candidateMate) => {
            mateById.set(
              candidateMate.mateId,
              transformMateDefinitionForSolve(candidateMate, componentDeltaMap, mutableComponents),
            );
          });
        }
        const result = applyMateDefinition(
          mateForCurrentStep,
          mate,
          mateById,
          concentricMisalignmentSolvePolicies,
          mutableComponents,
          componentDeltaMap,
          epsilon,
          solvePlan.componentGraphDistances,
          stationaryComponentIds,
          linearDimensionScale,
          distanceBranchPolicy,
          mateDefinitions,
          transientMateIds,
          transientMateIds.has(mate.mateId),
          useTransientInitialPlacement,
        );
        if (result.transform && result.movedComponentInstanceId) {
          const previousTransform = componentTransforms[result.movedComponentInstanceId];
          if (previousTransform && !areTransformsClose(previousTransform, result.transform, epsilon)) {
            const previousComponent = mutableComponents[result.movedComponentInstanceId]!;
            componentTransforms[result.movedComponentInstanceId] = result.transform;
            updateComponentWorldDeltaMap(
              componentDeltaMap,
              result.movedComponentInstanceId,
              previousTransform,
              result.transform,
            );
            const nextWorldTransform = updateWorldTransformFromLocalDelta(
              previousComponent.worldTransform,
              previousTransform,
              result.transform,
            );
            const previousEffectiveWorldTransform = previousComponent.worldTransform ?? previousTransform;
            const nextEffectiveWorldTransform = nextWorldTransform ?? result.transform;
            const nextWorldBounds = previousComponent.worldBounds
              ? transformStandardMateWorldBounds(
                  previousComponent.worldBounds,
                  createWorldDeltaBetweenTransforms(previousEffectiveWorldTransform, nextEffectiveWorldTransform),
                )
              : undefined;
            mutableComponents[result.movedComponentInstanceId] = {
              ...previousComponent,
              transform: result.transform,
              ...(nextWorldTransform ? { worldTransform: nextWorldTransform } : {}),
              ...(nextWorldBounds ? { worldBounds: nextWorldBounds } : {}),
            };
            hasTransformChanged = true;

            // 主组件属于浮动刚性组时，把同一世界 delta 联动到组内其余成员，保证刚体整体运动。
            const groupMemberIds = resolveRigidGroupMemberIds(rigidGroups, result.movedComponentInstanceId);
            if (groupMemberIds) {
              const groupWorldDelta = createWorldDeltaBetweenTransforms(
                previousEffectiveWorldTransform,
                nextEffectiveWorldTransform,
              );
              coMoveRigidGroupMembers({
                primaryComponentInstanceId: result.movedComponentInstanceId,
                worldDelta: groupWorldDelta,
                groupMemberIds,
                mutableComponents,
                componentTransforms,
                componentDeltaMap,
                epsilon,
              });
            }

            /**
             * 只有“本条 Mate 实际移动了该组件”才把它冻结为本轮已定位。
             *
             * 单条 Mate 通常只约束组件的部分自由度：例如同轴心只约束径向与朝向，仍保留沿轴平移；因此当同轴心已满足、
             * 未移动组件时不能冻结它，否则随后的重合会因组件被过早冻结而无法沿剩余轴向自由度滑到重合位置，被误判为
             * “重合无法满足”。已满足（未移动）的 Mate 并没有“摆放”该组件，就不应独占它的定位权。刚性组成员由联动刚体
             * 运动统一处理，同样不在此单独冻结。收敛性与过定义判定交由外层迭代和最终残差校验保证。
             */
            if (!rigidGroups.groupRootByComponentId.has(result.movedComponentInstanceId)) {
              stationaryComponentIds.add(result.movedComponentInstanceId);
            }
          }
        }
        iterationDiagnostics.push({
          mateId: mate.mateId,
          status: result.status,
          message: result.message,
          movedComponentInstanceId: result.movedComponentInstanceId,
        });
      });

      diagnostics = iterationDiagnostics;
      appendStandardMateSolverDebugTraceEvent(debugTrace, 'projection.iteration', {
        iterationIndex,
        hasTransformChanged,
        stationaryComponentIds: [...stationaryComponentIds],
        diagnostics: iterationDiagnostics,
      });
      if (!hasTransformChanged) {
        break;
      }
    }

    if (diagnostics.every((diagnostic) => diagnostic.status === MateSolveStatus.Solved)) {
      diagnostics.push(...validateSolvedMateDefinitions({
        mateDefinitions,
        components: mutableComponents,
        componentTransforms,
        componentDeltaMap,
        linearDimensionScale,
        transientMateIds: input.transientMateIds,
        epsilon,
        angleEpsilon: input.angleEpsilon,
      }));
    }

    const projectedResult = {
      status: diagnostics.length > 0
        ? resolveStandardMateSolverOverallStatus(diagnostics, transientMateIds)
        : MateSolveStatus.NotRun,
      componentTransforms,
      diagnostics,
    };
    appendStandardMateSolverDebugTraceEvent(debugTrace, 'projection.result', {
      status: projectedResult.status,
      diagnostics: projectedResult.diagnostics,
    });
    const finalResult = resolveStandardMateConstraintGraphSolve(input, projectedResult, debugTrace);
    guardStandardMateRigidInvariants(input.components, finalResult.componentTransforms);
    emitStandardMateSolverDebugTrace(debugTrace, finalResult);
    return finalResult;
  }
}

/**
 * 求解后自执行刚体不变量守卫（B 方案）。
 *
 * 说明：
 * 逐组件比对求解前后缩放与四元数模长，任何违例都是“求解偷改了本应不变的刚体属性”的确证信号。
 * dev 构建（未显式关闭时）对违例 `console.error` 告警，让未来任何绕过 {@link applyWorldDeltaToTransform}
 * 收口的位姿写回路径立刻暴露；生产构建不抛错、不打断用户求解，只依赖测试对回放场景断言零违例来兜底。
 *
 * @param beforeComponents 求解前组件状态（含 local transform）。
 * @param afterComponentTransforms 求解后组件 local transform 表。
 */
function guardStandardMateRigidInvariants(
  beforeComponents: StandardMateSolverInput['components'],
  afterComponentTransforms: Record<string, ComponentTransformSnapshot>,
): void {
  const isInvariantGuardDisabled =
    (globalThis as { __PUPPYCAD_DISABLE_MATE_RIGID_INVARIANT_GUARD__?: boolean })
      .__PUPPYCAD_DISABLE_MATE_RIGID_INVARIANT_GUARD__ === true;
  if (isInvariantGuardDisabled) {
    return;
  }

  const before: Record<string, ComponentTransformSnapshot> = {};
  for (const [componentInstanceId, component] of Object.entries(beforeComponents)) {
    before[componentInstanceId] = component.transform;
  }

  const violations = collectStandardMateRigidInvariantViolations({
    before,
    after: afterComponentTransforms,
  });
  if (violations.length > 0) {
    // eslint-disable-next-line no-console
    console.error('[PUPPYCAD_MATE_RIGID_INVARIANT_VIOLATION]', JSON.stringify(violations));
  }
}
